import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { LEYENDAS_ANALISIS } from "@/modules/analisis_financiero/leyendas";
import { ejemploParaMes, EJEMPLO_GIRO } from "@/modules/analisis_financiero/ejemploCarga";
import {
  borrarMes,
  descargarPlantilla,
  guardarEstado,
  guardarGiro,
  obtenerAnalisis,
  previsualizarArchivo,
  previsualizarManual,
} from "@/modules/analisis_financiero/analisisFinancieroApi";
import { etiquetaOrigen } from "@/modules/analisis_financiero/giroConstanciaUi";
import { claseSemaforo, moneyMx, pctMx } from "@/modules/analisis_financiero/semaforoUi";

const RUBRO_IGNORAR = "ignorar";

function etiquetaIntegridad(status) {
  if (status === "verified") return "Integridad verificada";
  if (status === "missing") return "Integridad incompleta";
  return "Pendiente de verificar";
}

export default function GastosRazonablesTab({
  clientId,
  ejercicio,
  mes,
  giro,
  setGiro,
  rubros,
  puedeEscribir,
  onGuardado,
  giroVersion = 0,
}) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [textoManual, setTextoManual] = useState("");
  const [mostrarManual, setMostrarManual] = useState(false);
  const [analisis, setAnalisis] = useState(null);
  const [auditMsg, setAuditMsg] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargarAnalisis = async (giroScian = giro.giro_scian) => {
    if (!clientId) return;
    try {
      const { data } = await obtenerAnalisis({
        clientId,
        ejercicio,
        mes,
        giroScian,
      });
      setAnalisis(data);
    } catch (err) {
      if (err?.response?.status !== 404) {
        toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo leer el análisis.");
      }
    }
  };

  useEffect(() => {
    setPreview(null);
    setAnalisis(null);
    if (clientId) cargarAnalisis(giro.giro_scian);
    // Se recalcula al cambiar empresa, mes o al guardar el giro, no en cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, ejercicio, mes, giroVersion]);

  const recibirPreview = (data) => {
    if (data && data.ok === false) {
      setPreview(null);
      toast.error(data.mensaje || "No se pudo leer el archivo.");
      return;
    }
    setPreview(data);
    (data?.advertencias || []).forEach((msg) => toast.message(msg));
  };

  const abrirArchivo = (modo) => {
    const input = fileRef.current;
    if (!input || !puedeEscribir) return;
    input.accept = modo === "pdf"
      ? ".pdf,application/pdf"
      : ".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    input.click();
  };

  const onArchivo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !puedeEscribir) return;
    try {
      const { data } = await previsualizarArchivo({ file, clientId });
      recibirPreview(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo leer el archivo.");
    }
  };

  const onManual = async () => {
    if (!puedeEscribir) return;
    try {
      const { data } = await previsualizarManual({ clientId, texto: textoManual });
      recibirPreview(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se reconoció el pegado.");
    }
  };

  const onEjemplo = () => {
    if (!puedeEscribir) return;
    setGiro(EJEMPLO_GIRO);
    setPreview({
      ok: true,
      formato: "manual",
      archivo_nombre: "ejemplo-ficticio",
      advertencias: ["Cifras de EJEMPLO, solo para ver el flujo. No son datos de un cliente."],
      renglones: ejemploParaMes(mes),
    });
    toast.message("Se cargó un estado de resultados de ejemplo. Revise el mapeo y guárdelo.");
  };

  const cambiarRubro = (idx, rubro) => {
    setPreview((prev) => {
      if (!prev) return prev;
      const renglones = prev.renglones.map((row, i) => (
        i === idx
          ? { ...row, rubro, ignorar: rubro === RUBRO_IGNORAR, rubro_origen: "usuario" }
          : row
      ));
      return { ...prev, renglones };
    });
  };

  const guardar = async () => {
    if (!puedeEscribir || !clientId || !preview?.renglones?.length) {
      toast.error("Elija una empresa y un estado de resultados antes de guardar.");
      return;
    }
    setGuardando(true);
    try {
      if (giro.giro_scian) {
        await guardarGiro(clientId, giro);
      }
      const origen = preview.formato === "pdf" ? "pdf" : (preview.formato === "manual" ? "manual" : "excel");
      await guardarEstado({
        client_id: clientId,
        ejercicio: Number(ejercicio),
        mes: Number(mes),
        tipo: "real",
        origen,
        archivo_nombre: preview.archivo_nombre || "",
        archivo_sha256: preview.archivo_sha256 || "",
        renglones: preview.renglones.map((row) => ({
          etiqueta_original: row.etiqueta_original,
          importe: row.importe,
          mes: row.mes || Number(mes),
          rubro: row.ignorar ? RUBRO_IGNORAR : row.rubro,
          ignorar: Boolean(row.ignorar) || row.rubro === RUBRO_IGNORAR,
        })),
      });
      toast.success("Estado de resultados guardado. Hay un solo importe por rubro en este mes.");
      setPreview(null);
      await cargarAnalisis(giro.giro_scian);
      if (onGuardado) onGuardado();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo guardar el estado.");
    } finally {
      setGuardando(false);
    }
  };

  const limpiarMes = async () => {
    if (!puedeEscribir || !clientId) return;
    try {
      await borrarMes({ clientId, ejercicio: Number(ejercicio), mes: Number(mes) });
      toast.success("Se borró el estado de resultados de este mes.");
      setAnalisis(null);
      await cargarAnalisis(giro.giro_scian);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo borrar.");
    }
  };

  const plantilla = async () => {
    try {
      const { data } = await descargarPlantilla();
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "estado_resultados_plantilla.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo descargar la plantilla.");
    }
  };

  const forzarAuditoria = async () => {
    if (!clientId) return;
    setAuditMsg("Verificando integridad…");
    try {
      const { data } = await api.post("/internal/audit", {
        module_name: "analisis_financiero",
        client_id: clientId,
        full_scan: false,
        trigger_healing: false,
      });
      setAuditMsg(data?.message || "Auditoría terminada.");
      toast.success("Auditoría del estado de resultados terminada.");
      await cargarAnalisis(giro.giro_scian);
    } catch (err) {
      setAuditMsg("");
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo forzar la auditoría.");
    }
  };

  const opcionesRubro = [
    { id: RUBRO_IGNORAR, etiqueta: "Ignorar (subtotal o utilidad)" },
    ...(rubros || []),
  ];

  return (
    <div className="space-y-5" data-testid="tab-gastos-razonables">
      <div className="rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950 space-y-1" data-testid="leyendas-analisis">
        {LEYENDAS_ANALISIS.map((texto) => <p key={texto}>{texto}</p>)}
      </div>

      <p className="text-xs text-slate-600" data-testid="giro-aplicado">
        Giro aplicado al semáforo: {giro?.giro_scian || "sin giro"}
        {giro?.giro_descripcion ? ` · ${giro.giro_descripcion}` : ""}. {etiquetaOrigen(giro?.origen_giro)}.
        Los rangos por giro se editan en Configuración de datos y están marcados como ejemplo.
      </p>

      <div className="flex flex-wrap gap-2" data-testid="carga-estado">
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.pdf"
          className="hidden"
          onChange={onArchivo}
          data-testid="input-archivo"
        />
        <button
          type="button"
          disabled={!puedeEscribir}
          className="rounded-sm bg-[#0F2D5C] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          onClick={() => abrirArchivo("excel")}
          data-testid="btn-cargar-excel"
        >
          Cargar Excel
        </button>
        <button
          type="button"
          disabled={!puedeEscribir}
          className="rounded-sm border border-[#0F2D5C] px-3 py-1.5 text-xs font-semibold text-[#0F2D5C] disabled:opacity-50"
          onClick={() => abrirArchivo("pdf")}
          data-testid="btn-cargar-pdf"
        >
          Cargar PDF
        </button>
        <button
          type="button"
          disabled={!puedeEscribir}
          className="rounded-sm border px-3 py-1.5 text-xs disabled:opacity-50"
          onClick={() => setMostrarManual((v) => !v)}
          data-testid="btn-captura-manual"
        >
          Captura o pegado manual
        </button>
        <button
          type="button"
          disabled={!puedeEscribir}
          className="rounded-sm border border-amber-400 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-950 disabled:opacity-50"
          onClick={onEjemplo}
          data-testid="btn-ejemplo"
        >
          Cargar ejemplo
        </button>
        <button type="button" className="rounded-sm border px-3 py-1.5 text-xs" onClick={plantilla}>
          Descargar plantilla
        </button>
        {puedeEscribir ? (
          <button type="button" className="rounded-sm border px-3 py-1.5 text-xs text-red-800" onClick={limpiarMes} data-testid="btn-borrar-mes">
            Borrar este mes
          </button>
        ) : (
          <span className="text-xs text-slate-500 self-center">Consulta: solo lectura. No puede cargar, guardar ni borrar.</span>
        )}
      </div>

      {mostrarManual ? (
        <div className="space-y-2" data-testid="captura-manual">
          <p className="text-xs text-slate-600">
            Pegue desde Excel (concepto, importe y mes opcional, separados por tabulador) o escriba un renglón por línea.
          </p>
          <textarea
            className="w-full min-h-28 rounded-sm border px-2 py-1 text-sm"
            value={textoManual}
            onChange={(e) => setTextoManual(e.target.value)}
            placeholder={"Ventas netas\t1000000\nSueldos y salarios\t350000"}
            data-testid="texto-manual"
          />
          <button type="button" className="rounded-sm border px-3 py-1.5 text-xs" onClick={onManual} disabled={!puedeEscribir}>
            Previsualizar pegado
          </button>
        </div>
      ) : null}

      {preview?.renglones?.length ? (
        <section className="rounded-sm border border-[#0F2D5C]/20 p-3 space-y-3" data-testid="vista-previa-mapeo">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-[#0F2D5C]">Vista previa y mapeo de renglones</h3>
            <button
              type="button"
              disabled={!puedeEscribir || guardando}
              className="rounded-sm bg-[#0F2D5C] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              onClick={guardar}
              data-testid="btn-guardar-estado"
            >
              {guardando ? "Guardando…" : "Guardar estado de resultados"}
            </button>
          </div>
          <p className="text-xs text-slate-600">
            El mapeo se recuerda por empresa. Debe existir Ventas netas mayores a cero. Un rubro por mes: si se repite, se suma.
          </p>
          {(preview.advertencias || []).map((msg) => (
            <p key={msg} className="text-xs text-amber-900">{msg}</p>
          ))}
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="py-1 pr-2">Renglón original</th>
                  <th className="py-1 pr-2">Importe</th>
                  <th className="py-1 pr-2">Mes</th>
                  <th className="py-1">Rubro estándar</th>
                </tr>
              </thead>
              <tbody>
                {preview.renglones.map((row, idx) => (
                  <tr key={`${row.etiqueta_original}-${idx}`} className="border-t border-slate-100" data-testid={`mapeo-${idx}`}>
                    <td className="py-1 pr-2">{row.etiqueta_original}</td>
                    <td className="py-1 pr-2">{moneyMx(row.importe)}</td>
                    <td className="py-1 pr-2">{row.mes || mes}</td>
                    <td className="py-1">
                      <select
                        className="rounded-sm border px-2 py-1"
                        value={row.ignorar ? RUBRO_IGNORAR : (row.rubro || "")}
                        onChange={(e) => cambiarRubro(idx, e.target.value)}
                        disabled={!puedeEscribir}
                        data-testid={`mapeo-rubro-${idx}`}
                      >
                        <option value="">Sin asignar</option>
                        {opcionesRubro.map((r) => (
                          <option key={r.id} value={r.id}>{r.etiqueta}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="space-y-2" data-testid="resultado-gastos">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-[#0F2D5C]">Resultado del mes</h3>
          <button
            type="button"
            className="rounded-sm border px-3 py-1.5 text-xs"
            onClick={forzarAuditoria}
            disabled={!clientId}
            data-testid="btn-forzar-auditoria"
          >
            Forzar auditoría
          </button>
        </div>
        {auditMsg ? <p className="text-xs text-slate-600" data-testid="audit-msg">{auditMsg}</p> : null}
        {!analisis?.renglones?.length ? (
          <p className="text-sm text-slate-500" data-testid="resultado-vacio">
            Este mes todavía no tiene estado de resultados. Cargue un archivo, péguelo o use el ejemplo.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-slate-200">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-2 py-2 text-left">Rubro</th>
                  <th className="px-2 py-2 text-right">Importe</th>
                  <th className="px-2 py-2 text-right">% de ventas</th>
                  <th className="px-2 py-2 text-left">Rango de referencia</th>
                  <th className="px-2 py-2 text-left">Semáforo</th>
                  <th className="px-2 py-2 text-left">Fuente</th>
                  <th className="px-2 py-2 text-left">Integridad</th>
                </tr>
              </thead>
              <tbody>
                {analisis.renglones.map((row) => (
                  <tr key={row.renglon_id} className="border-t border-slate-100" data-testid={`resultado-${row.renglon_id}`}>
                    <td className="px-2 py-2">
                      <div className="font-medium">{row.etiqueta}</div>
                      <div className="text-slate-500">{row.renglon_original}</div>
                    </td>
                    <td className="px-2 py-2 text-right">{moneyMx(row.importe)}</td>
                    <td className="px-2 py-2 text-right">{pctMx(row.pct)}</td>
                    <td className="px-2 py-2">
                      {row.renglon_id === "ventas_netas"
                        ? "Base"
                        : `${pctMx(row.minimo_pct)} a ${pctMx(row.maximo_pct)} · muy alto ${pctMx(row.muy_alto_pct)}`}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`rounded-sm px-2 py-0.5 font-semibold ${claseSemaforo(row)}`} data-testid={`semaforo-${row.renglon_id}`}>
                        {row.etiqueta_estado}
                      </span>
                      {row.es_ejemplo && row.etiqueta_estado !== "EJEMPLO" ? (
                        <span className="ml-1 rounded-sm bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-950">EJEMPLO</span>
                      ) : null}
                    </td>
                    <td className="px-2 py-2 max-w-[220px]">
                      {row.url ? (
                        <a href={row.url} className="underline text-[#0F2D5C]" target="_blank" rel="noreferrer">{row.fuente}</a>
                      ) : (row.fuente || "—")}
                      {row.nota ? <div className="text-slate-500">{row.nota}</div> : null}
                    </td>
                    <td className="px-2 py-2" title={etiquetaIntegridad(row.integrity_status)}>
                      {row.integrity_status === "verified" ? "✓" : row.integrity_status === "missing" ? "⚠" : "⟳"}
                      <span className="ml-1">{etiquetaIntegridad(row.integrity_status)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {analisis?.ventas_netas ? (
          <p className="text-xs text-slate-600">Ventas netas del mes: {moneyMx(analisis.ventas_netas)}. Tipo real. El mismo estado alimenta la pestaña de proyección.</p>
        ) : null}
      </section>

    </div>
  );
}
